// Intel Hex parser from https://github.com/benjaminaigner/avr109-webserial
const DATA = 0, END_OF_FILE = 1, EXT_SEGMENT_ADDR = 2, START_SEGMENT_ADDR = 3, EXT_LINEAR_ADDR = 4, START_LINEAR_ADDR = 5;
const EMPTY_VALUE = 0xFF;

const fromHexString = hexString => new Uint8Array(hexString.match(/.{1,2}/g).map(byte => parseInt(byte, 16)));

function parseIntelHex(data) {
    data = data.toString("ascii");
    var buf = new Uint8Array(32768);
    var bufLength = 0, highAddress = 0, startSegmentAddress = null, startLinearAddress = null, lineNum = 0, pos = 0;
    const SMALLEST_LINE = 11;
    while (pos + SMALLEST_LINE <= data.length) {
        if (data.charAt(pos++) != ":") throw new Error("Line " + (lineNum + 1) + " does not start with a colon (:).");
        else lineNum++;
        var dataLength = parseInt(data.substr(pos, 2), 16); pos += 2;
        var lowAddress = parseInt(data.substr(pos, 4), 16); pos += 4;
        var recordType = parseInt(data.substr(pos, 2), 16); pos += 2;
        var dataField = data.substr(pos, dataLength * 2);
        if (dataLength) var dataFieldBuf = fromHexString(dataField); else var dataFieldBuf = new Uint8Array();
        pos += dataLength * 2;
        var checksum = parseInt(data.substr(pos, 2), 16); pos += 2;
        var calcChecksum = (dataLength + (lowAddress >> 8) + lowAddress + recordType) & 0xFF;
        for (var i = 0; i < dataLength; i++) calcChecksum = (calcChecksum + dataFieldBuf[i]) & 0xFF;
        calcChecksum = (0x100 - calcChecksum) & 0xFF;
        if (checksum != calcChecksum) throw new Error("Invalid checksum on line " + lineNum);
        switch (recordType) {
            case DATA:
                var absoluteAddress = highAddress + lowAddress;
                if (absoluteAddress > bufLength) buf.fill(EMPTY_VALUE, bufLength, absoluteAddress);
                dataFieldBuf.forEach(function (val, index) { buf[absoluteAddress + index] = val; });
                bufLength = Math.max(bufLength, absoluteAddress + dataLength);
                break;
            case END_OF_FILE:
                if (dataLength != 0) throw new Error("Invalid EOF record on line " + lineNum);
                return { data: buf.slice(0, bufLength), startSegmentAddress, startLinearAddress };
            case EXT_SEGMENT_ADDR:
                if (dataLength != 2 || lowAddress != 0) throw new Error("Invalid extended segment address record on line " + lineNum);
                highAddress = parseInt(dataField, 16) << 4; break;
            case START_SEGMENT_ADDR:
                if (dataLength != 4 || lowAddress != 0) throw new Error("Invalid start segment address record on line " + lineNum);
                startSegmentAddress = parseInt(dataField, 16); break;
            case EXT_LINEAR_ADDR:
                if (dataLength != 2 || lowAddress != 0) throw new Error("Invalid extended linear address record on line " + lineNum);
                highAddress = parseInt(dataField, 16) << 16; break;
            case START_LINEAR_ADDR:
                if (dataLength != 4 || lowAddress != 0) throw new Error("Invalid start linear address record on line " + lineNum);
                startLinearAddress = parseInt(dataField, 16); break;
            default:
                throw new Error("Invalid record type (" + recordType + ") on line " + lineNum);
        }
        if (data.charAt(pos) == "\r") pos++;
        if (data.charAt(pos) == "\n") pos++;
    }
    throw new Error("Unexpected end of input: missing or invalid EOF record.");
}

const equals = (a, b) => a.length === b.length && a.every((v, i) => v === b[i]);
const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Flash firmware via AVR109 protocol over Web Serial.
 * @param {File} hexFile - .hex file to flash
 * @param {object} opts
 * @param {function} opts.onProgress - callback(status: string)
 * @param {AbortSignal} opts.signal - optional abort signal
 */
export async function flashLeonardo(hexFile, { onProgress, signal } = {}) {
    if (!("serial" in navigator)) throw new Error("Web Serial API not supported");
    
    const log = msg => { if (onProgress) onProgress(msg); console.log(msg); };
    const checkAbort = () => { if (signal && signal.aborted) throw new Error("Aborted"); };

    // 1. Read hex file
    checkAbort();
    log("Loading .hex file...");
    const text = await hexFile.text();
    const flashData = parseIntelHex(text);
    log(`Valid .hex file: ${flashData.data.length} bytes`);

    // 2. Connect to bootloader — user must press RESET on Arduino first
    checkAbort();
    log("Click RESET button on Arduino, then select bootloader port");
    // No USB filters — user picks the bootloader port manually
    const port = await navigator.serial.requestPort();
    await port.open({ baudRate: 57600 });
    const writer = port.writable.getWriter();
    const reader = port.readable.getReader();

    let portOpen = true;
    const cleanup = async () => {
        if (portOpen) {
            portOpen = false;
            try { reader.releaseLock(); } catch (e) { }
            try { writer.releaseLock(); } catch (e) { }
            try { await port.close(); } catch (e) { }
        }
    };

    try {
        checkAbort();
        
        // Send programmer ID request
        log("Bước 2: Loading...");
        await writer.write(new Uint8Array([0x53])); // 'S'
        await wait(5);

        let state = 0;
        let pageStart = 0;
        let address = 0;
        let totalPages = Math.ceil(flashData.data.length / 128);
        let pageCount = 0;

        while (true) {
            const { value, done } = await reader.read();
            if (done) throw new Error("Serial port closed unexpectedly");
            checkAbort();

            if (state === 0) {
                // Wait for "CATERIN"
                if (equals(value, [67, 65, 84, 69, 82, 73, 78])) {
                    log("Programmer CATERIN detected, entering programming mode...");
                    await writer.write(new Uint8Array([0x50])); // 'P'
                    await wait(5);
                    state = 1;
                } else {
                    log(`Unexpected response: ${Array.from(value)}`);
                }
            } else if (state === 1) {
                // Entered programming mode, set address
                if (value[0] === 13) {
                    log(`Flashing page ${pageCount + 1}/${totalPages}...`);
                    let data = new Uint8Array([0x41, (address >> 8) & 0xFF, address & 0xFF]); // 'A' high low
                    await writer.write(data);
                    await wait(5);
                    state = 2;
                }
            } else if (state === 2) {
                // Write page
                if (value[0] === 13) {
                    let cmd = new Uint8Array([0x42, 0x00, 0x80, 0x46]); // 'B' + 2bytes size + 'F'
                    let data, txx;
                    if (pageStart + 128 > flashData.data.length) {
                        data = flashData.data.slice(pageStart);
                        let pad = new Uint8Array(128 - data.length);
                        pad.fill(0xFF);
                        txx = Uint8Array.from([...cmd, ...data, ...pad]);
                        state = 3;
                    } else {
                        data = flashData.data.slice(pageStart, pageStart + 128);
                        txx = Uint8Array.from([...cmd, ...data]);
                        state = 1;
                    }
                    pageStart += 128;
                    address += 64;
                    pageCount++;
                    await writer.write(txx);
                    await wait(5);
                }
            } else if (state === 3) {
                // Last page sent
                if (value[0] === 13) {
                    log("Last page written, leaving programming mode...");
                    await writer.write(new Uint8Array([0x4C])); // 'L' leave
                    state = 4;
                }
            } else if (state === 4) {
                if (value[0] === 13) {
                    log("Exiting bootloader...");
                    await writer.write(new Uint8Array([0x45])); // 'E' exit
                    state = -1;
                }
            } else if (state === -1) {
                log("✅ Flashed!");
                await reader.cancel();
                break;
            }
        }
    } finally {
        await cleanup();
    }
}